import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { Loader2, Tags, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DescriptionGrouper({ groups, onChanged }) {
  const { can } = useAuth();
  const [kind, setKind] = useState("expense");
  const [onlyUngrouped, setOnlyUngrouped] = useState(true);
  const [rows, setRows] = useState([]);
  const [choice, setChoice] = useState({});
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/transactions/description-summary`, {
      params: { kind, ungrouped_only: onlyUngrouped },
    });
    setRows(data.rows);
  }, [kind, onlyUngrouped]);

  useEffect(() => { load().catch(() => setNotice("Liste alınamadı")); }, [load]);

  const options = kind === "income" ? (groups.income || []) : (groups.expense || []);

  const apply = async row => {
    const group = choice[row.description];
    if (!group) return;
    setBusy(row.description);
    setNotice("");
    try {
      const { data } = await axios.patch(`${API}/transactions/by-description`, {
        description: row.description, kind, group, remember: true,
      });
      setNotice(`"${row.description}" → ${group} · ${data.updated} kayıt güncellendi, kural kaydedildi.`);
      await load();
      onChanged?.();
    } catch (e) {
      setNotice(e?.response?.data?.detail || "Atama başarısız");
    } finally {
      setBusy("");
    }
  };

  const removeRule = async row => {
    setBusy(row.description);
    try {
      await axios.delete(`${API}/transactions/description-rule`, { params: { description: row.description, kind } });
      await load();
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="panel grouper-panel" data-testid="description-grouper">
      <div className="panel-head">
        <div>
          <p className="eyebrow">AÇIKLAMAYA GÖRE GRUPLAMA</p>
          <h3>Aynı açıklamalı kayıtları tek tıkla grupla</h3>
          <small className="hint">
            Seçtiğin grup, o açıklamaya sahip <b>geçmiş tüm kayıtlara</b> uygulanır ve kural olarak kaydedilir —
            bundan sonra aynı açıklamayla girdiğin kayıtlar da otomatik bu gruba düşer.
          </small>
        </div>
        <Tags size={18}/>
      </div>

      <div className="grouper-filters">
        <select data-testid="grouper-kind" value={kind} onChange={e => setKind(e.target.value)}>
          <option value="expense">Giderler</option>
          <option value="income">Gelirler</option>
        </select>
        <label className="check-line">
          <input type="checkbox" data-testid="grouper-ungrouped" checked={onlyUngrouped}
            onChange={e => setOnlyUngrouped(e.target.checked)}/>
          <span>Sadece gruplanmamış ("Diğer") olanlar</span>
        </label>
        <span className="grouper-count" data-testid="grouper-count">{rows.length} açıklama</span>
      </div>

      {notice && <div className="notice" data-testid="grouper-notice">{notice}</div>}

      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Açıklama</th><th className="right">Kayıt</th><th>Dönem</th><th>Şu anki grup</th><th>Yeni grup</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.description} data-testid={`grouper-row-${row.description}`}>
                <td><b>{row.description}</b>{row.rule_group && <small className="sub-line">kural: {row.rule_group}</small>}</td>
                <td className="right">{row.count}</td>
                <td><small>{row.first_date} → {row.last_date}</small></td>
                <td>{row.groups.join(", ")}</td>
                <td>
                  <select data-testid={`grouper-select-${row.description}`} value={choice[row.description] || ""}
                    disabled={!can("edit")}
                    onChange={e => setChoice(cur => ({ ...cur, [row.description]: e.target.value }))}>
                    <option value="">Grup seç…</option>
                    {options.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </td>
                <td className="row-actions">
                  <button className="primary-btn tiny" data-testid={`grouper-apply-${row.description}`}
                    disabled={!can("edit") || !choice[row.description] || busy === row.description}
                    onClick={() => apply(row)}>
                    {busy === row.description ? <Loader2 size={13} className="spin"/> : "Tümüne uygula"}
                  </button>
                  {row.rule_group && can("edit") && (
                    <button className="icon-btn danger" title="Kuralı kaldır" data-testid={`grouper-rule-remove-${row.description}`}
                      onClick={() => removeRule(row)}><X size={14}/></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <div className="empty-table" data-testid="grouper-empty">
          {onlyUngrouped ? "Gruplanmamış kayıt kalmadı." : "Kayıt yok."}
        </div>}
      </div>
    </section>
  );
}
